import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Text, EnhancedTable, Avatar } from "@pushpress/shared-components";
import type { DataTableSort } from "@pushpress/shared-components/build/components/data/DataTable";
import { ClubParticipant } from "./types";
import { getInitials } from "../../utils/misc";
import { Badge } from "@pushpress/committed-club-typescript-sdk";
import { CircleCheck } from "lucide-react";

interface CommittedClubParticipantsTableProps {
  badge: Badge;
  data?: ClubParticipant[];
  loading: boolean;
}

function CommittedClubParticipantsTable({
  badge,
  data,
  loading,
}: CommittedClubParticipantsTableProps) {
  const [members, setMembers] = useState<ClubParticipant[]>(data || []);

  const [sort, setSort] = useState<DataTableSort<string>>({
    key: "checkinCount",
    order: "desc",
  });

  useEffect(() => setMembers(data), [data]);

  const getRowId = useCallback((row: ClubParticipant) => row.uuid, []);

  const onSort = useCallback(
    (newSort: DataTableSort<string>) => {
      const nextSort: DataTableSort<string> =
        sort?.key != newSort?.key
          ? {
              key: newSort.key,
              order: newSort.key === "memberName" ? "asc" : "desc",
            }
          : newSort;

      const nextMembers = data.slice();

      if (nextSort.key === "memberName") {
        nextMembers.sort((a, b) => {
          if (nextSort.order == "asc") {
            return a.name > b.name ? 1 : -1;
          } else {
            return a.name > b.name ? -1 : 1;
          }
        });
      } else if (nextSort.key === "checkinCount") {
        nextMembers.sort((a, b) => {
          if (nextSort.order == "asc") {
            if (a.totalCount > b.totalCount) {
              return 1;
            } else if (a.totalCount < b.totalCount) {
              return -1;
            } else {
              return a.name > b.name ? 1 : -1;
            }
          } else {
            if (a.totalCount > b.totalCount) {
              return -1;
            } else if (a.totalCount < b.totalCount) {
              return 1;
            } else {
              return a.name > b.name ? 1 : -1;
            }
          }
        });
      }

      setSort(nextSort);
      setMembers(nextMembers);
    },
    [data, sort?.key],
  );

  const columns = useMemo(
    () => [
      {
        key: "memberName",
        header: "Name",
        isSortable: true,
        hideOnMobile: false,
        width: "60%",
        cell: (row: ClubParticipant) => {
          const initials = getInitials(...row.name.split(" "));
          return (
            <div className={"tw-flex tw-items-center tw-space-x-3"}>
              <div className="tw-flex-shrink-0">
                <Avatar size="small">
                  {row.profileImage ? (
                    <img src={row.profileImage} alt={`${row.name} avatar`} />
                  ) : (
                    initials
                  )}
                </Avatar>
              </div>
              <div>
                <a href={`/members/view/${row.uuid}`}>
                  <Text variant="body-lg" color="pacific-primary">
                    {row.name}
                  </Text>
                </a>
                <Text
                  variant="body-md"
                  color="wolf-600"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {row.email}
                </Text>
              </div>
            </div>
          );
        },
      },
      {
        key: "checkinCount",
        header: "Check-ins",
        isSortable: true,
        hideOnMobile: false,
        width: "40%",
        cell: (row: ClubParticipant) => {
          return (
            <div className="tw-flex tw-space-x-3">
              <Text variant="heading-5" color="wolf-800">
                {row.totalCount}
              </Text>
              <div className="tw-w-3">
                {row.totalCount >= badge.criteriaTotal && (
                  <CircleCheck fill="black" color="white" size={24} />
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [badge.criteriaTotal],
  );

  return (
    <EnhancedTable
      columns={columns}
      data={members}
      sort={sort}
      loading={loading}
      getRowId={getRowId}
      onSort={onSort}
    />
  );
}

export default CommittedClubParticipantsTable;
